import { createConnector } from 'wagmi'

import { abstractWalletConnector } from '@abstract-foundation/agw-react/connectors'

import { allChains } from '@src/configs/chainConfig'
import { QUICKNODE_RPCS } from '@src/configs/rpc'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('SafeAbstractWallet')

/**
 * A safe wrapper around the Abstract Wallet connector that never throws errors.
 * If the connector encounters an unsupported chain or any other error, it returns
 * a dummy connector that fails gracefully instead of throwing errors.
 */
export const safeAbstractWalletConnector = () => {
    try {
        // Get the original connector
        const originalConnector = abstractWalletConnector()

        // Return a wrapper function that matches the signature of the original
        return (config: any) => {
            try {
                // Get the original connector instance
                const connector = originalConnector(config)

                // Create a safer version that catches errors
                return {
                    ...connector,
                    // Wrap getProvider to catch "Unsupported chain" errors
                    getProvider: async (params: { chainId?: number } = {}) => {
                        try {
                            // Try to get the provider from the original connector
                            const provider = await connector.getProvider(params)
                            return provider
                        } catch (error) {
                            // Log the error but don't throw
                            logger.error('Error in Abstract Wallet connector getProvider:', {
                                error,
                                chainId: params?.chainId,
                                message: error instanceof Error ? error.message : 'Unknown error',
                            })

                            // Return a dummy provider that won't cause runtime errors
                            return {
                                on: () => {},
                                removeListener: () => {},
                                request: async () => {
                                    // Return dummy responses that won't crash the app
                                    return null
                                },
                            }
                        }
                    },
                    // Wrap other methods as needed to prevent errors
                    connect: async (params: any = {}) => {
                        try {
                            return await connector.connect(params)
                        } catch (error) {
                            logger.error('Error in Abstract Wallet connector connect:', { error })
                            // Return a dummy response
                            return { accounts: [], chainId: 1 }
                        }
                    },
                    disconnect: async () => {
                        try {
                            await connector.disconnect()
                        } catch (error) {
                            logger.error('Error in Abstract Wallet connector disconnect:', { error })
                        }
                    },
                }
            } catch (error) {
                // If there's an error creating the connector, return a properly configured fallback connector
                logger.error('Error creating Abstract Wallet connector instance:', { error })
                return createFallbackConnector()(config)
            }
        }
    } catch (error) {
        // If there's an error initializing the connector, return a function that returns a properly configured fallback connector
        logger.error('Failed to initialize Abstract Wallet connector:', { error })
        // Return a function that will create the fallback connector when called with proper config
        return (config: any) => createFallbackConnector()(config)
    }
}

/**
 * Creates a properly configured fallback connector that implements the required interface.
 * This prevents runtime errors when the Abstract Wallet connector fails, while still
 * providing proper chain configuration using wagmi chains and QUICKNODE_RPCS.
 */
const createFallbackConnector = () => {
    // Create a connector that can handle chains not supported by Abstract Wallet
    return createConnector(({ chains }) => ({
        id: 'xyz.abs.privy.safe.fallback',
        name: 'Abstract Wallet (Fallback)',
        type: 'injected',
        icon: undefined,
        rdns: 'xyz.abs.privy.safe.fallback',
        // Configure with all available chains
        chains: allChains.map(chain => {
            // If we have custom RPC URLs for this chain, use them
            if (QUICKNODE_RPCS[chain.id as keyof typeof QUICKNODE_RPCS]) {
                return {
                    ...chain,
                    rpcUrls: {
                        ...chain.rpcUrls,
                        default: {
                            http: QUICKNODE_RPCS[chain.id as keyof typeof QUICKNODE_RPCS],
                        },
                        public: {
                            http: QUICKNODE_RPCS[chain.id as keyof typeof QUICKNODE_RPCS],
                        },
                    },
                }
            }
            return chain
        }),
        getProvider: async ({ chainId } = {}) => {
            logger.debug('Fallback connector getProvider called', { chainId })
            // Return a dummy provider that logs operations instead of failing
            return {
                on: (event: string, listener: any) => {
                    logger.debug('Fallback provider event listener added', { event })
                    return {}
                },
                removeListener: (event: string, listener: any) => {
                    logger.debug('Fallback provider event listener removed', { event })
                    return {}
                },
                request: async ({ method, params }: any) => {
                    logger.debug('Fallback provider request', { method, params, chainId })
                    // Return dummy responses that won't crash the app
                    if (method === 'eth_chainId') {
                        return chainId ? `0x${chainId.toString(16)}` : '0x1'
                    }
                    if (method === 'eth_accounts') {
                        return []
                    }
                    return null
                },
            }
        },
        connect: async ({ chainId } = {}) => {
            logger.debug('Fallback connector connect called', { chainId })
            // Return a dummy response
            return { accounts: [], chainId: chainId || 1 }
        },
        disconnect: async () => {
            logger.debug('Fallback connector disconnect called')
        },
        isAuthorized: async () => false,
        onConnect: (handler: any) => {
            logger.debug('Fallback connector onConnect handler added')
            return () => {}
        },
        onAccountsChanged: (handler: any) => {
            logger.debug('Fallback connector onAccountsChanged handler added')
            return () => {}
        },
        onChainChanged: (handler: any) => {
            logger.debug('Fallback connector onChainChanged handler added')
            return () => {}
        },
        onDisconnect: (handler: any) => {
            logger.debug('Fallback connector onDisconnect handler added')
            return () => {}
        },
        getAccounts: async () => [],
        getChainId: async () => 1,
        onMessage: () => {},
        supportsSimulation: false,
        setupListeners: () => {},
        removeListeners: () => {},
    }))
}
